import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/leadershipSeminar";
import { IconUserCheck } from "../../../icons/IconUserCheck/IconUserCheck";
import LeafTile from "../../../leaf/LeafTile/LeafTile";
import "./NewLegTile.scss";

export interface NewLegRequirement {
  Name: string;
  Met: boolean;
  Target: number;
  Current: number;
  Remaining: number;
}

export interface NewLegTileProps {
  requirement: NewLegRequirement;
}

export const NewLegTile = ({ requirement }: NewLegTileProps) => {
  const { t } = useTranslation(["leadershipSeminar"]);

  // Render person-with-checkmark icons: green for completed, gray for remaining
  const renderPersonIcons = () =>
    Array.from({ length: requirement.Target }, (_, i) => {
      const isCompleted = i < requirement.Current;
      const label = isCompleted
        ? String(t("pathView.completedLeg", defaultContent.pathView.completedLeg))
        : String(t("pathView.remainingLeg", defaultContent.pathView.remainingLeg));

      return (
        <IconUserCheck
          key={i}
          color={isCompleted ? "green" : "gray"}
          aria-label={`${label} ${i + 1} of ${requirement.Target}`}
          title={label}
        />
      );
    });

  return (
    <div className="NewLegTile__container">
      <LeafTile
        title={String(t("pathView.newLegTitle", defaultContent.pathView.newLegTitle, { target: requirement.Target }))}
        showMetStatus={true}
        met={requirement.Met}
        hideBorder={false}
        circleCheckmarkId={`ls-path-newleg-${requirement.Name}`}
      >
        <div className="NewLegTile">
          {/* Information section - icons and description with 12px gap */}
          <div className="NewLegTile__information">
            <div
              className="NewLegTile__icons"
              role="group"
              aria-label={String(t("pathView.newLegIconsLabel", defaultContent.pathView.newLegIconsLabel))}
            >
              {renderPersonIcons()}
            </div>
            <p className="NewLegTile__description">
              {String(
                t("pathView.newLegDescription", defaultContent.pathView.newLegDescription, {
                  target: requirement.Target,
                }),
              )}
            </p>
          </div>
          {/* KPI section - 24px gap from information, 8px gap between rows */}
          <dl className="NewLegTile__kpis">
            <div className="NewLegTile__kpi-row">
              <dt>{String(t("pathView.currentYtd", defaultContent.pathView.currentYtd))}</dt>
              <dd className={requirement.Met ? "NewLegTile__kpi-value--met" : "NewLegTile__kpi-value--not-met"}>
                {requirement.Current}
              </dd>
            </div>
            <div className="NewLegTile__kpi-row">
              <dt>{String(t("pathView.needed", defaultContent.pathView.needed))}</dt>
              <dd className="NewLegTile__kpi-value--needed">{requirement.Remaining}</dd>
            </div>
          </dl>
        </div>
      </LeafTile>
    </div>
  );
};
